/**
 * SectionChip — "you are here", and the two rooms either side of you.
 *
 * The depth viewer's doorway is the good way between sections, but it is a gesture, and a
 * gesture nobody finds is not a feature. This is the visible twin: it says which room is open
 * and how many there are, and its chevrons make the same move the doorway does. On desktop,
 * where nobody is pushing a finger against a rim, they are the ONLY way across.
 *
 * Top-CENTRE, because top-left is the toolbar and the bag button and the left column is
 * already three deep.
 */
import type { SectionGraph } from '@/hooks/useSectionGraph'

interface SectionChipProps {
  graph: SectionGraph
  zenMode: boolean
  /** Step to an adjacent room, by node key. The host owns the transition and the loading. */
  onStep: (key: string) => void
  /** Tap the label — hands off to the full picker rather than growing a second one here. */
  onOpenPicker: () => void
  /** True while a section is loading, so the chip cannot queue up three switches. */
  busy?: boolean
  /** Where in this section's walk the viewer is, and how long the walk is. */
  frameIndex: number
  frameCount: number
}

export function SectionChip({
  graph, zenMode, onStep, onOpenPicker, busy, frameIndex, frameCount,
}: SectionChipProps) {
  const { currentIndex, total, next, prev } = graph
  if (total < 2 || currentIndex < 0) return null

  const current = graph.ordered[currentIndex]

  // Sections join end to start, so the two ends of the walk are where the doorways are. Marking
  // them turns "why did pushing here do something" into "I can see I am at the edge of the room"
  // — the progress line is the only thing on screen that says a crossing is available at all.
  const walk = frameCount > 1 ? frameIndex / (frameCount - 1) : 0
  const atExit =
    frameCount > 0 && ((frameIndex >= frameCount - 1 && !!next) || (frameIndex <= 0 && !!prev))

  return (
    <div
      style={{
        position: 'fixed',
        top: 'calc(12px + env(safe-area-inset-top, 0px))',
        left: '50%',
        zIndex: 600,
        display: 'flex',
        alignItems: 'center',
        gap: 2,
        // Extra bottom padding is the progress line's room — without it the line sits under the
        // chevrons rather than below them.
        padding: '3px 4px 7px',
        maxWidth: 'min(80vw, 380px)',
        background: 'rgba(20,20,20,0.78)',
        border: '1px solid rgba(255,255,255,0.12)',
        borderRadius: 22,
        backdropFilter: 'blur(10px)',
        WebkitBackdropFilter: 'blur(10px)',
        boxShadow: '0 4px 16px rgba(0,0,0,0.5)',
        opacity: zenMode ? 0 : 1,
        pointerEvents: zenMode ? 'none' : 'auto',
        // translateX(-50%) has to survive into the zen transform or the chip slides off-centre
        // on its way out.
        transform: zenMode
          ? 'translateX(-50%) translateY(-14px) scale(0.9)'
          : 'translateX(-50%)',
        transition: 'opacity 0.28s ease, transform 0.28s ease',
      }}
    >
      <button
        onClick={() => prev && onStep(prev.key)}
        disabled={!prev || busy}
        style={stepBtn(!!prev && !busy)}
        title={prev ? `Back to ${prev.name}` : 'This is the first section'}
      >‹</button>

      <button
        onClick={onOpenPicker}
        style={{
          display: 'flex',
          alignItems: 'baseline',
          gap: 6,
          minWidth: 0,
          padding: '0 6px',
          height: 30,
          background: 'transparent',
          border: 'none',
          borderRadius: 16,
          color: '#fff',
          cursor: 'pointer',
          WebkitTapHighlightColor: 'transparent',
        }}
        title="All sections"
      >
        <span
          style={{
            fontSize: 13,
            fontWeight: 600,
            letterSpacing: '.01em',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >{current.name}</span>
        <span style={{ fontSize: 11, fontWeight: 500, color: '#9ca3af', flexShrink: 0 }}>
          {currentIndex + 1}/{total}
        </span>
        {/* Which frame of the walk. Dimmer than the section counter and behind a separator:
            two "n/m" pairs side by side otherwise read as one number nobody can parse. */}
        {frameCount > 0 && (
          <span style={{ fontSize: 10, fontWeight: 500, color: '#6b7280', flexShrink: 0 }}>
            · {frameIndex + 1}/{frameCount}
          </span>
        )}
      </button>

      <button
        onClick={() => next && onStep(next.key)}
        disabled={!next || busy}
        style={stepBtn(!!next && !busy)}
        title={next ? `On to ${next.name}` : 'This is the last section'}
      >›</button>

      {/* Where you are in this section's walk, along the bottom rim of the chip. A number tells
          you the frame; this tells you how close the way out is, which is the thing the doorway
          gesture needs you to know. It goes green at either end, where a crossing exists. */}
      {frameCount > 1 && (
        <div
          style={{
            position: 'absolute',
            left: 12,
            right: 12,
            bottom: 3,
            height: 2,
            borderRadius: 1,
            background: 'rgba(255,255,255,0.12)',
            overflow: 'hidden',
            pointerEvents: 'none',
          }}
        >
          <div
            style={{
              width: `${Math.max(2, walk * 100)}%`,
              height: '100%',
              borderRadius: 1,
              background: atExit ? '#34d399' : 'rgba(255,255,255,0.45)',
              boxShadow: atExit ? '0 0 6px rgba(52,211,153,0.8)' : 'none',
              transition: 'width 0.18s ease, background 0.28s ease, box-shadow 0.28s ease',
            }}
          />
        </div>
      )}
    </div>
  )
}

export default SectionChip

/** Dimmed rather than hidden at the ends: a chip that changes width as you walk is worse. */
const stepBtn = (enabled: boolean): React.CSSProperties => ({
  width: 30,
  height: 30,
  flexShrink: 0,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  background: 'transparent',
  border: 'none',
  borderRadius: '50%',
  color: enabled ? '#fff' : '#9ca3af',
  opacity: enabled ? 1 : 0.35,
  fontSize: 20,
  lineHeight: 1,
  padding: 0,
  cursor: enabled ? 'pointer' : 'default',
  WebkitTapHighlightColor: 'transparent',
})
